import type { FC, ReactNode } from 'react'
import type { RegisterUpdater } from '@/components/landing/film/overlay-utils'

import { useEffect, useRef } from 'react'
import { cn } from '@/lib/utils'
import { ease, progress } from '@/components/landing/film/timeline'
import Odometer from '@/components/landing/film/scenes/odometer'

import {
    fixed,
    setAttr,
    setLayer,
    setStyle
} from '@/components/landing/film/overlay-utils'

interface SceneEngineeringProps {
    register: RegisterUpdater
}

interface Callout {
    id: string
    anchor: 'spoke' | 'hub' | 'lip'
    side: 'left' | 'right'
    landscape: [number, number]
    portrait: [number, number]
    start: number
    eyebrow: string
    value: string
    unit?: string
    unitBefore?: boolean
    label: string
}

const CALLOUTS: Callout[] = [
    {
        id: 'material',
        anchor: 'spoke',
        side: 'left',
        landscape: [0.06, 0.19],
        portrait: [0.07, 0.085],
        start: 5.35,
        eyebrow: '01 · Forged from billet',
        value: '6061-T6',
        label: 'Aerospace aluminum'
    },
    {
        id: 'offset',
        anchor: 'hub',
        side: 'right',
        landscape: [0.94, 0.4],
        portrait: [0.93, 0.25],
        start: 6.5,
        eyebrow: '02 · Custom offset',
        value: '-15 / +45',
        unit: 'ET',
        unitBefore: true,
        label: 'Specified to your hub and fender line'
    },
    {
        id: 'weight',
        anchor: 'lip',
        side: 'left',
        landscape: [0.06, 0.63],
        portrait: [0.07, 0.77],
        start: 7.6,
        eyebrow: '03 · Weight',
        value: '8.4',
        unit: 'KG',
        label: 'Ultra light forged monoblock'
    }
]

const BOX_WIDTH = { landscape: 0.26, portrait: 0.46 }
const FADE_OUT: [number, number] = [8.72, 9.05]

// Scene 02: engineering callouts track points on the spinning wheel while their numbers roll up
const SceneEngineering: FC<SceneEngineeringProps> = ({ register }): ReactNode => {
    const layerRef = useRef<HTMLDivElement>(null)
    const svgRef = useRef<SVGSVGElement>(null)
    const boxRefs = useRef<Array<HTMLDivElement | null>>([])
    const ruleRefs = useRef<Array<HTMLSpanElement | null>>([])
    const lineRefs = useRef<Array<SVGPathElement | null>>([])
    const dotRefs = useRef<Array<SVGCircleElement | null>>([])
    const ringRefs = useRef<Array<SVGCircleElement | null>>([])

    useEffect(() => register((t, real, frame) => {
        const active = t >= 5.25 && t < FADE_OUT[1]
        setLayer(layerRef.current, active ? 1 : 0)
        if (!active) return

        const width = frame.width
        const height = frame.height
        const fade = 1 - progress(t, FADE_OUT[0], FADE_OUT[1])
        const boxWidth = (frame.portrait ? BOX_WIDTH.portrait : BOX_WIDTH.landscape) * width
        setAttr(svgRef.current, 'viewBox', `0 0 ${Math.round(width)} ${Math.round(height)}`)

        CALLOUTS.forEach((callout, i) => {
            const box = boxRefs.current[i]
            const [px, py] = frame.portrait ? callout.portrait : callout.landscape
            const enter = ease.outExpo(progress(t, callout.start, callout.start + 0.7))
            const draw = ease.outCubic(progress(t, callout.start + 0.15, callout.start + 0.8))

            setStyle(box, 'width', `${fixed(boxWidth, 1)}px`)
            setStyle(box, 'top', `${fixed(py * 100, 2)}%`)
            if (callout.side === 'left') setStyle(box, 'left', `${fixed(px * 100, 2)}%`)
            else setStyle(box, 'right', `${fixed((1 - px) * 100, 2)}%`)
            setStyle(box, 'opacity', fixed(enter * fade, 3))
            setStyle(box, 'transform', `translateY(${fixed((1 - enter) * 14, 2)}px)`)
            setStyle(ruleRefs.current[i], 'transform', `scaleX(${fixed(draw, 3)})`)

            const anchor = frame[callout.anchor]
            const x0 = callout.side === 'left' ? px * width + boxWidth : px * width - boxWidth
            const y0 = py * height + 8
            const x1 = x0 + (callout.side === 'left' ? 1 : -1) * width * 0.03
            const line = lineRefs.current[i]
            setAttr(line, 'd', `M ${fixed(x0, 1)} ${fixed(y0, 1)} L ${fixed(x1, 1)} ${fixed(y0, 1)} L ${fixed(anchor.x, 1)} ${fixed(anchor.y, 1)}`)
            const lineDraw = ease.outCubic(progress(t, callout.start + 0.35, callout.start + 0.95))
            setStyle(line, 'stroke-dashoffset', fixed(1 - lineDraw, 4))
            setStyle(line, 'opacity', anchor.visible ? fixed(fade * 0.85, 3) : '0')

            const dotLevel = anchor.visible ? progress(t, callout.start + 0.8, callout.start + 1) * fade : 0
            setAttr(dotRefs.current[i], 'cx', fixed(anchor.x, 1))
            setAttr(dotRefs.current[i], 'cy', fixed(anchor.y, 1))
            setStyle(dotRefs.current[i], 'opacity', fixed(dotLevel, 3))
            const pulse = (real * 0.9 + i * 0.33) % 1
            setAttr(ringRefs.current[i], 'cx', fixed(anchor.x, 1))
            setAttr(ringRefs.current[i], 'cy', fixed(anchor.y, 1))
            setAttr(ringRefs.current[i], 'r', fixed(4 + pulse * 12, 2))
            setStyle(ringRefs.current[i], 'opacity', fixed(dotLevel * (1 - pulse), 3))
        })
    }), [register])

    return (
        <div ref={layerRef} className='pointer-events-none absolute inset-0' style={{ opacity: 0, visibility: 'hidden' }}>
            <svg ref={svgRef} className='absolute inset-0 size-full overflow-visible' viewBox='0 0 100 100' aria-hidden='true'>
                {CALLOUTS.map((callout, i) => (
                    <g key={callout.id}>
                        <path
                            ref={(element) => {
                                lineRefs.current[i] = element
                            }}
                            d='M 0 0'
                            fill='none'
                            stroke='#ffd700'
                            strokeWidth='1'
                            pathLength={1}
                            strokeDasharray='1'
                            strokeDashoffset='1'
                            style={{ opacity: 0 }}
                        />
                        <circle
                            ref={(element) => {
                                ringRefs.current[i] = element
                            }}
                            r='4'
                            fill='none'
                            stroke='#ffd700'
                            strokeWidth='1'
                            style={{ opacity: 0 }}
                        />
                        <circle
                            ref={(element) => {
                                dotRefs.current[i] = element
                            }}
                            r='3.5'
                            fill='#ffd700'
                            style={{ opacity: 0 }}
                        />
                    </g>
                ))}
            </svg>

            {CALLOUTS.map((callout, i) => {
                const right = callout.side === 'right'

                return (
                    <div
                        key={callout.id}
                        ref={(element) => {
                            boxRefs.current[i] = element
                        }}
                        className={cn('absolute', right && 'text-right')}
                        style={{ opacity: 0 }}
                    >
                        <div className={cn('flex items-center gap-3', right && 'flex-row-reverse')}>
                            <span className='film-eyebrow whitespace-nowrap text-gold'>
                                {callout.eyebrow}
                            </span>
                            <span
                                ref={(element) => {
                                    ruleRefs.current[i] = element
                                }}
                                className={cn('h-px flex-1 bg-gold/70', right ? 'origin-right' : 'origin-left')}
                                style={{ transform: 'scaleX(0)' }}
                            />
                        </div>

                        <div className={cn('film-value mt-[1.2cqh] flex items-end gap-[0.22em] text-white', right && 'justify-end')}>
                            {callout.unit && callout.unitBefore && (
                                <span className='pb-[0.08em] text-[0.42em] tracking-[0.18em] text-titanium-light'>
                                    {callout.unit}
                                </span>
                            )}
                            <Odometer value={callout.value} start={callout.start + 0.1} duration={1.1} register={register} />
                            {callout.unit && !callout.unitBefore && (
                                <span className='pb-[0.08em] text-[0.42em] tracking-[0.18em] text-titanium-light'>
                                    {callout.unit}
                                </span>
                            )}
                        </div>

                        <p className='film-body mt-[1cqh] text-titanium-light'>
                            {callout.label}
                        </p>
                    </div>
                )
            })}
        </div>
    )
}

export default SceneEngineering
